import { useCallback, useMemo, useState } from "react";
import { debounce } from "lodash";

import { useOnStoreChange } from "@/Common/CustomHooks/useOnStoreChange";
import AIScribeAction from "../Api/AIScribeAction";
import usePostMessage from "../Hooks/usePostMessage";

import styles from "./AIScribe.module.scss";

type AIScribeMetaDataType =
    "recording_started"
    | "recording_stopped"
    | "generation_succeeded"
    | "generation_failed"
    | "resize"
    | "change_locale"
    | "no_sound_detected"
    | "sound_detected"
    | "microphone_blocked"
    | "microphone_detection_incomplete"
    | "microphone_detected"
    | "maximized"
    | "unmaximized";

interface AIScribePopupMetaData {
    type: AIScribeMetaDataType;
    source?: string;
}

interface AIScribePopupResize extends AIScribePopupMetaData {
    type: "resize";
    width: number;
    height: number;
}

interface AIScribePopupRecordingStopped extends AIScribePopupMetaData {
    type: "recording_stopped";
    duration: number;
}

interface AIScribePopupGenerationFailed extends AIScribePopupMetaData {
    type: "generation_failed";
    reason: string;
}

interface AIScribePopupChangeLanguage extends AIScribePopupMetaData {
    type: "change_locale";
    locale: string;
}

interface AIScribePopupContentProps {
    iframeRef: React.RefObject<HTMLIFrameElement>;
    consultationId: string;
    url: string;
    isVisible: boolean;

    onNoteGenerated: () => void;
    onNoteGeneratedFailed: (reason: string) => void;
    onRecordingStarted: () => void;
    onRecordingStopped: (duration: number) => void;
    onSizeChanged: () => void;
    onSoundDetection: (hasSound: boolean) => void;
    onMicrophoneBlocked: () => void;
    onMicrophoneDetectionIncomplete: () => void;
    onMicrophoneDeviceDetected?: () => void;
    onMaximizeChanged: (isMaximized: boolean) => void;
}

interface IframeSize {
    width: number;
    height: number;
}

function AIScribePopupContent(props: Readonly<AIScribePopupContentProps>): JSX.Element {
    const source = "ai_scribe_integration";
    const aiScribeAction = useMemo(() => new AIScribeAction(), []);

    const iframeRef = props.iframeRef;
    const [contentSize, setContentSize] = useState({ width: 384, height: 230 } as IframeSize);
    const [lastSentLanguage, setLastSentLanguage] = useState<string | null>(null);
    const [recordingDuration, setRecordingDuration] = useState(0);

    usePostMessage(handleOnPostMessage, isAValidEvent);

    function isAValidEvent(message: AIScribePopupMetaData, eventOrigin: string, eventSource: EventTarget): boolean {
        if (message.source !== source ||
            originOfUrl(eventOrigin) !== originOfUrl(props.url) ||
            eventSource !== iframeRef.current.contentWindow)
            return false;

        return true;
    }

    useOnStoreChange(
        settingsStore,
        () => {
            const language = settingsStore.configuration.get("Language") === "fr" ? "fr" : "en";

            if (language !== lastSentLanguage) {
                const message: AIScribePopupChangeLanguage = {
                    type: "change_locale",
                    locale: language,
                    source: "ai_scribe_integration"
                };
                setLastSentLanguage(language);
                iframeRef.current?.contentWindow?.postMessage(message, new URL(props.url).origin);
            }
        }
    );

    async function handleOnPostMessage(message: AIScribePopupMetaData): Promise<void> {
        switch (message.type) {
            case "recording_started":
                onRecordingStarted();
                break;
            case "recording_stopped":
                await onRecordingStopped(message as AIScribePopupRecordingStopped);
                break;
            case "generation_succeeded":
                props.onNoteGenerated?.();
                break;
            case "generation_failed":
                await onNoteGenerationFailed(message as AIScribePopupGenerationFailed);
                break;
            case "resize":
                resizeIframe(message as AIScribePopupResize);
                break;
            case "no_sound_detected":
                onSoundDetection(false);
                break;
            case "sound_detected":
                onSoundDetection(true);
                break;
            case "microphone_blocked":
                onMicrophoneBlocked();
                break;
            case "microphone_detection_incomplete":
                onMicrophoneDetectionIncomplete();
                break;
            case "microphone_detected":
                onMicrophoneDeviceDetected();
                break;
            case "maximized":
                props.onMaximizeChanged(true);
                break;
            case "unmaximized":
                props.onMaximizeChanged(false);
                break;
            default:
                console.error("Unknown message type: ", message.type);
                break;
        }
    }

    function resizeIframe(size: AIScribePopupResize): void {
        setContentSize({ ...size });
        props.onSizeChanged?.();
    }

    function onRecordingStarted(): void {
        props.onRecordingStarted?.();
    }

    const updateRecordingDurationOnServer = useCallback(
        async (id: string, duration: number) => await aiScribeAction.updateConsultationRecordingDuration(id, duration),
        [aiScribeAction]
    );

    const debouncedUpdateRecordingDuration = useMemo(
        () => debounce(updateRecordingDurationOnServer, 5000),
        [updateRecordingDurationOnServer]
    );

    async function onRecordingStopped({ duration }: AIScribePopupRecordingStopped): Promise<void> {
        if (!duration)
            return;

        const newDuration = recordingDuration + duration;
        setRecordingDuration(newDuration);

        await debouncedUpdateRecordingDuration(props.consultationId, newDuration);

        props.onRecordingStopped?.(newDuration);
    }

    const updateFailureReasonOnServer = useCallback(
        async (id: string, reason: string) => await aiScribeAction.updateConsultationGenerationFailureReason(id, reason),
        [aiScribeAction]
    );

    const debouncedUpdateFailureReason = useMemo(
        () => debounce(updateFailureReasonOnServer, 3000),
        [updateFailureReasonOnServer]
    );

    async function onNoteGenerationFailed({ reason }: AIScribePopupGenerationFailed): Promise<void> {
        if (!reason)
            return;

        await debouncedUpdateFailureReason(props.consultationId, reason);

        props.onNoteGeneratedFailed?.(reason);
    }

    function onSoundDetection(hasSound: boolean): void {
        props.onSoundDetection?.(hasSound);
    }

    function onMicrophoneBlocked(): void {
        props.onMicrophoneBlocked?.();
    }

    function onMicrophoneDetectionIncomplete(): void {
        props.onMicrophoneDetectionIncomplete?.();
    }

    function onMicrophoneDeviceDetected(): void {
        props.onMicrophoneDeviceDetected?.();
    }

    function originOfUrl(url: string): string {
        const domain = new URL(url);
        return domain.origin;
    }

    return (
        /**
         * Currently, the iframe uses timers to detect sound and handle event of stop-talking.
         * If the iframe is hidden, the browser throttles these timers, causing delays in sound detection and other functionalities.
         * To prevent this, we set a minimal height for the iframe to keep it visible but hidden from the user.
         * This ensures that the iframe remains active and responsive, allowing it to function correctly even when not in view.
         */
        <div data-testid="ai-scribe-iframe-container" className={props.isVisible ? "" : styles.aiScribeIframeContainerHidden}>
            <iframe
                ref={iframeRef}
                id="ai-scribe-popup-content"
                data-cy="ai-scribe-popup-content"
                title="ai scribe"
                src={props.url}
                allow="microphone 'src'"
                width={contentSize.width}
                height={contentSize.height}
            />
        </div>
    );
}

export { AIScribePopupContent };
